// src/pages/pmc/PmcFinalRaApproval.js
import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  query,
  where,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebaseConfig";
import toast, { Toaster } from "react-hot-toast";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const cx = (...xs) => xs.filter(Boolean).join(" ");
const toNum = (v) =>
  Number(String(v || "").replace(/[^0-9.\-]/g, "")) || 0;

export default function PmcFinalRaApproval() {
  const navigate = useNavigate();
  const auth = useMemo(() => getAuth(), []);

  const [user, setUser] = useState(null);

  const [tenders, setTenders] = useState([]);
  const [loadingTenders, setLoadingTenders] = useState(true);
  const [selectedTenderId, setSelectedTenderId] = useState("");

  const [tender, setTender] = useState(null);
  const [contractor, setContractor] = useState(null);

  const [boqItems, setBoqItems] = useState([]);
  const [contractorRas, setContractorRas] = useState([]);
  const [engineerRas, setEngineerRas] = useState([]);
  const [finalRas, setFinalRas] = useState([]);

  const [selectedContractorId, setSelectedContractorId] = useState("");
  const [selectedEngineerId, setSelectedEngineerId] = useState("");
  const [finalRows, setFinalRows] = useState([]);
  const [finalDate, setFinalDate] = useState(
    dayjs().format("YYYY-MM-DD")
  );
  const [saving, setSaving] = useState(false);
  const [loadingData, setLoadingData] = useState(false);

  /* ---------- AUTH GUARD ---------- */
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      if (!u) {
        toast.error("Please login to PMC dashboard.");
        navigate("/pmc/dashboard", { replace: true });
        return;
      }
      setUser(u);
    });
    return () => unsub();
  }, [auth, navigate]);

  /* ---------- LOAD ALL AWARDED TENDERS FOR DROPDOWN ---------- */
  useEffect(() => {
    const loadTenders = async () => {
      if (!user) return;
      try {
        setLoadingTenders(true);
        const snap = await getDocs(collection(db, "tenders"));
        const list = [];
        snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
        const awarded = list.filter((t) => t.awardedVendorUid);
        awarded.sort(
          (a, b) =>
            (b.createdAt?.seconds || 0) -
            (a.createdAt?.seconds || 0)
        );
        setTenders(awarded);
      } catch (e) {
        console.error(e);
        toast.error("Failed to load tenders for Final RA.");
      } finally {
        setLoadingTenders(false);
      }
    };
    loadTenders();
  }, [user]);

  /* ---------- LOAD DATA FOR SELECTED TENDER ---------- */
  useEffect(() => {
    const loadAllForTender = async () => {
      if (!selectedTenderId) {
        setTender(null);
        setContractor(null);
        setBoqItems([]);
        setContractorRas([]);
        setEngineerRas([]);
        setFinalRas([]);
        setFinalRows([]);
        return;
      }

      try {
        setLoadingData(true);

        // Tender
        const tSnap = await getDoc(
          doc(db, "tenders", selectedTenderId)
        );
        if (!tSnap.exists()) {
          toast.error("Tender not found.");
          return;
        }
        const t = { id: tSnap.id, ...tSnap.data() };
        setTender(t);

        // Contractor
        if (t.awardedVendorUid) {
          const vSnap = await getDoc(
            doc(db, "vendors", t.awardedVendorUid)
          );
          if (vSnap.exists()) {
            setContractor({ id: vSnap.id, ...vSnap.data() });
          } else {
            setContractor(null);
          }
        } else {
          setContractor(null);
        }

        // BOQ
        const boqSnap = await getDocs(
          collection(db, "tenders", selectedTenderId, "boq_items")
        );
        const boqList = [];
        boqSnap.forEach((d) => boqList.push({ id: d.id, ...d.data() }));
        boqList.sort((a, b) => (a.itemNo || 0) - (b.itemNo || 0));
        setBoqItems(boqList);

        // Contractor RA
        const cRef = query(
          collection(db, "ra_bills"),
          where("tenderId", "==", selectedTenderId)
        );
        const cSnap = await getDocs(cRef);
        const cList = [];
        cSnap.forEach((d) => cList.push({ id: d.id, ...d.data() }));
        cList.sort(
          (a, b) =>
            (a.createdAt?.seconds || 0) -
            (b.createdAt?.seconds || 0)
        );
        setContractorRas(cList);

        // Engineer RA
        const eRef = query(
          collection(db, "ra_bills_engineer"),
          where("tenderId", "==", selectedTenderId)
        );
        const eSnap = await getDocs(eRef);
        const eList = [];
        eSnap.forEach((d) => eList.push({ id: d.id, ...d.data() }));
        eList.sort(
          (a, b) =>
            (b.createdAt?.seconds || 0) -
            (a.createdAt?.seconds || 0)
        );
        setEngineerRas(eList);

        // Final RA
        const fRef = query(
          collection(db, "ra_bills_final"),
          where("tenderId", "==", selectedTenderId)
        );
        const fSnap = await getDocs(fRef);
        const fList = [];
        fSnap.forEach((d) => fList.push({ id: d.id, ...d.data() }));
        fList.sort(
          (a, b) =>
            (a.createdAt?.seconds || 0) -
            (b.createdAt?.seconds || 0)
        );
        setFinalRas(fList);

        // reset selection
        setSelectedContractorId("");
        setSelectedEngineerId("");
        setFinalRows([]);
      } catch (e) {
        console.error(e);
        toast.error("Failed to load Final RA data.");
      } finally {
        setLoadingData(false);
      }
    };

    if (user) loadAllForTender();
  }, [user, selectedTenderId]);

  /* ---------- BUILD FINAL ROWS WHEN BOTH RA SELECTED ---------- */
  useEffect(() => {
    if (!tender || !boqItems.length) {
      setFinalRows([]);
      return;
    }
    if (!selectedContractorId && !selectedEngineerId) {
      setFinalRows([]);
      return;
    }

    const contractorRa = contractorRas.find(
      (r) => r.id === selectedContractorId
    );
    const engineerRa = engineerRas.find(
      (r) => r.id === selectedEngineerId
    );

    const contractorMap = {};
    if (contractorRa) {
      (contractorRa.items || []).forEach((it) => {
        contractorMap[it.itemId] = it;
      });
    }
    const engineerMap = {};
    if (engineerRa) {
      (engineerRa.items || []).forEach((it) => {
        engineerMap[it.itemId] = it;
      });
    }

    const rows = boqItems.map((boq) => {
      const cItem = contractorMap[boq.id] || {};
      const eItem = engineerMap[boq.id] || {};

      const cQty = toNum(cItem.qtyThisRa || cItem.finalQty || 0);
      const eQty = toNum(eItem.qtyThisRa || eItem.finalQty || 0);

      let defaultFinal = 0;
      if (cQty > 0 && eQty > 0) defaultFinal = Math.min(cQty, eQty);
      else defaultFinal = cQty || eQty;

      const boqQty = toNum(boq.boqQty);
      const rate = toNum(boq.rateWithLC || boq.rate || 0);

      let maxAllowed = boqQty;
      if (cQty > 0) maxAllowed = Math.min(maxAllowed, cQty);
      if (eQty > 0) maxAllowed = Math.min(maxAllowed, eQty);
      if (defaultFinal > maxAllowed) defaultFinal = maxAllowed;

      return {
        itemId: boq.id,
        itemNo: boq.itemNo,           // ✅ yahi line galat thi (boj.itemNo)
        description: boq.description,
        unit: boq.unit || "",
        boqQty,
        contractorQty: cQty,
        engineerQty: eQty,
        maxAllowed,
        finalQty: defaultFinal,
        rate,
        amount: defaultFinal * rate,
      };
    });

    setFinalRows(rows);
  }, [
    selectedContractorId,
    selectedEngineerId,
    contractorRas,
    engineerRas,
    boqItems,
  ]);

  const handleFinalQtyChange = (itemId, value) => {
    setFinalRows((prev) =>
      prev.map((r) => {
        if (r.itemId !== itemId) return r;
        let v = toNum(value);
        if (v < 0) v = 0;
        if (v > r.maxAllowed) {
          toast.error(
            `Item ${r.itemNo}: Max allowed ${r.maxAllowed.toFixed(
              3
            )} ${r.unit} (BOQ / Contractor / Engineer limit).`
          );
          v = r.maxAllowed;
        }
        return {
          ...r,
          finalQty: v,
          amount: v * r.rate,
        };
      })
    );
  };

  const finalTotal = finalRows.reduce(
    (sum, r) => sum + (r.amount || 0),
    0
  );

  /* ---------- SAVE FINAL RA + PDF ---------- */
  const handleSaveFinalRa = async () => {
    if (!selectedTenderId) {
      toast.error("Select a project first.");
      return;
    }
    if (!selectedContractorId && !selectedEngineerId) {
      toast.error("Select Contractor RA and/or Engineer RA.");
      return;
    }
    const items = finalRows.filter((r) => r.finalQty > 0);
    if (!items.length) {
      toast.error("Final qty zero for all items.");
      return;
    }

    try {
      setSaving(true);

      const nextIndex = finalRas.length + 1;
      const raNo = `FINAL-RA-${String(nextIndex).padStart(2, "0")}`;

      const payload = {
        tenderId: selectedTenderId,
        tenderTitle: tender?.title || tender?.projectName || "",
        contractorName:
          contractor?.profile?.companyName ||
          tender?.awardedVendorName ||
          "",
        contractorRaNo:
          contractorRas.find((r) => r.id === selectedContractorId)
            ?.raNo || "",
        engineerRaNo:
          engineerRas.find((r) => r.id === selectedEngineerId)?.raNo ||
          "",
        raNo,
        raDate: finalDate,
        totalAmount: finalTotal,
        status: "approved",
        createdAt: serverTimestamp(),
        items: items.map((r) => ({
          itemId: r.itemId,
          itemNo: r.itemNo,
          description: r.description,
          unit: r.unit,
          boqQty: r.boqQty,
          contractorQty: r.contractorQty,
          engineerQty: r.engineerQty,
          finalQty: r.finalQty,
          rate: r.rate,
          amount: r.amount,
        })),
      };

      const docRef = await addDoc(
        collection(db, "ra_bills_final"),
        payload
      );

      toast.success(`Final RA ${raNo} saved & PDF generated.`);

      setFinalRas((prev) => [...prev, { ...payload, id: docRef.id }]);

      generateFinalRaPdf(tender, payload);
    } catch (e) {
      console.error(e);
      toast.error("Failed to save Final RA.");
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
    navigate("/pmc/dashboard", { replace: true });
  };

  /* ---------- RENDER ---------- */

  if (!user) {
    return (
      <>
        <Toaster position="top-center" />
        <div className="min-h-screen flex items-center justify-center bg-emerald-50">
          <div className="text-sm text-slate-600">
            Loading Final RA Approval…
          </div>
        </div>
      </>
    );
  }

  const contractorRaSelected = contractorRas.find(
    (r) => r.id === selectedContractorId
  );
  const engineerRaSelected = engineerRas.find(
    (r) => r.id === selectedEngineerId
  );



  return (
    <>
      <Toaster position="top-center" />
      <div className="min-h-screen bg-emerald-50 pb-10">
        <div className="max-w-7xl mx-auto px-4 py-6">
          {/* HEADER */}
          <div className="rounded-2xl bg-white shadow-lg border border-emerald-200 px-6 py-5 mb-6 flex items-center justify-between">
            <div>
              <div className="text-xs text-gray-500 font-semibold uppercase tracking-wide">
                Final RA Approval – CEO / PMC
              </div>
              {tender ? (
                <>
                  <h1 className="text-xl md:text-2xl font-extrabold text-emerald-800">
                    {tender.title || tender.projectName}
                  </h1>
                  <div className="mt-1 text-[11px] text-slate-600">
                    {tender.ownerCompany || ""} · {tender.city || ""}
                  </div>
                </>
              ) : (
                <h1 className="text-lg font-semibold text-slate-700">
                  Select Project for Final RA Approval
                </h1>
              )}
            </div>
            <div className="flex flex-col items-end gap-2">
              <button
                onClick={handleLogout}
                className="text-xs px-3 py-2 rounded-lg bg-gray-900 text-white shadow hover:bg-black"
              >
                Logout
              </button>
              <Link
                to="/pmc/dashboard"
                className="text-[11px] text-emerald-700 underline"
              >
                ← Back to PMC Dashboard
              </Link>
            </div>
          </div>

          {/* PROJECT SELECTOR */}
          <div className="mb-4 bg-white border border-emerald-100 rounded-xl p-3 text-[11px]">
            <div className="text-slate-600 mb-1">
              Select Project / Tender
            </div>
            {loadingTenders ? (
              <div className="text-[11px] text-slate-500">
                Loading projects…
              </div>
            ) : tenders.length === 0 ? (
              <div className="text-[11px] text-slate-500">
                No awarded tenders found.
              </div>
            ) : (
              <select
                value={selectedTenderId}
                onChange={(e) =>
                  setSelectedTenderId(e.target.value)
                }
                className="w-full px-2 py-1.5 border rounded text-[11px]"
              >
                <option value="">— Select Project —</option>
                {tenders.map((t) => (
                  <option key={t.id} value={t.id}>
                    {(t.title || t.projectName || "Untitled") +
                      " · " +
                      (t.city || t.district || "") +
                      " · [" +
                      t.id +
                      "]"}
                  </option>
                ))}
              </select>
            )}
          </div>

          {loadingData && selectedTenderId && (
            <div className="text-[12px] text-slate-500 mb-4">
              Loading RA data for selected project…
            </div>
          )}

          {/* Agar project select nahi to yahin tak */}
          {!selectedTenderId && (
            <div className="text-[12px] text-slate-600">
              Please select a project from above to view Final RA Approval.
            </div>
          )}

          {selectedTenderId && !loadingData && (
            <>
              {/* RA SELECTION + SUMMARY */}
              <div className="mb-4 grid md:grid-cols-4 gap-4">
                <div className="bg-white border rounded-xl p-3 text-[11px]">
                  <div className="text-slate-500 mb-1">
                    Contractor RA
                  </div>
                  <select
                    value={selectedContractorId}
                    onChange={(e) =>
                      setSelectedContractorId(e.target.value)
                    }
                    className="w-full border rounded px-2 py-1 text-[11px]"
                  >
                    <option value="">— Select Contractor RA —</option>
                    {contractorRas.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.raNo || "RA"} · {r.raDate || ""} · ₹{" "}
                        {(r.totalAmount || 0).toLocaleString("en-IN")}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="bg-white border rounded-xl p-3 text-[11px]">
                  <div className="text-slate-500 mb-1">
                    Engineer RA
                  </div>
                  <select
                    value={selectedEngineerId}
                    onChange={(e) =>
                      setSelectedEngineerId(e.target.value)
                    }
                    className="w-full border rounded px-2 py-1 text-[11px]"
                  >
                    <option value="">— Select Engineer RA —</option>
                    {engineerRas.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.raNo || "RA"} · {r.raDate || ""} · ₹{" "}
                        {(r.totalAmount || 0).toLocaleString("en-IN")}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="bg-white border rounded-xl p-3 text-[11px]">
                  <div className="text-slate-500 mb-1">
                    Final RA Date
                  </div>
                  <input
                    type="date"
                    value={finalDate}
                    onChange={(e) => setFinalDate(e.target.value)}
                    className="w-full border rounded px-2 py-1 text-[11px]"
                  />
                </div>

                <div className="bg-white border rounded-xl p-3 text-[11px]">
                  <div className="text-slate-500 mb-1">
                    Final RA Amount
                  </div>
                  <div className="text-lg font-bold text-emerald-700">
                    ₹{" "}
                    {finalTotal.toLocaleString("en-IN", {
                      maximumFractionDigits: 2,
                    })}
                  </div>
                  <div className="text-[10px] text-slate-500 mt-1">
                    Based on selected Contractor & Engineer RAs
                  </div>
                </div>
              </div>

              {/* COMPARISON TABLE */}
              <div className="bg-white rounded-2xl border shadow-sm p-3 overflow-auto">
                {finalRows.length === 0 ? (
                  <div className="text-[12px] text-slate-500">
                    Select Contractor & Engineer RAs to compare item-wise.
                  </div>
                ) : (
                  <table className="min-w-full text-[11px] border border-slate-200">
                    <thead className="bg-emerald-50">
                      <tr>
                        <th className="border border-slate-200 px-2 py-1">
                          Item
                        </th>
                        <th className="border border-slate-200 px-2 py-1 text-left">
                          Description
                        </th>
                        <th className="border border-slate-200 px-2 py-1">
                          Unit
                        </th>
                        <th className="border border-slate-200 px-2 py-1 text-right">
                          BOQ
                        </th>
                        <th className="border border-slate-200 px-2 py-1 text-right">
                          Contractor Qty
                        </th>
                        <th className="border border-slate-200 px-2 py-1 text-right">
                          Engineer Qty
                        </th>
                        <th className="border border-slate-200 px-2 py-1 text-right">
                          Final Qty
                        </th>
                        <th className="border border-slate-200 px-2 py-1 text-right">
                          Rate
                        </th>
                        <th className="border border-slate-200 px-2 py-1 text-right">
                          Amount
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {finalRows.map((r) => (
                        <tr
                          key={r.itemId}
                          className="odd:bg-white even:bg-emerald-50/20"
                        >
                          <td className="border border-slate-200 px-2 py-1">
                            {r.itemNo}
                          </td>
                          <td className="border border-slate-200 px-2 py-1 text-left">
                            {r.description}
                          </td>
                          <td className="border border-slate-200 px-2 py-1 text-center">
                            {r.unit}
                          </td>
                          <td className="border border-slate-200 px-2 py-1 text-right">
                            {r.boqQty.toFixed(3)}
                          </td>
                          <td className="border border-slate-200 px-2 py-1 text-right">
                            {r.contractorQty.toFixed(3)}
                          </td>
                          <td className="border border-slate-200 px-2 py-1 text-right">
                            {r.engineerQty.toFixed(3)}
                          </td>
                          <td className="border border-slate-200 px-2 py-1 text-right">
                            <input
                              type="number"
                              step="0.001"
                              value={r.finalQty || ""}
                              onChange={(e) =>
                                handleFinalQtyChange(
                                  r.itemId,
                                  e.target.value
                                )
                              }
                              className="w-24 px-1 py-0.5 border rounded-md text-right"
                            />
                          </td>
                          <td className="border border-slate-200 px-2 py-1 text-right">
                            {r.rate.toFixed(2)}
                          </td>
                          <td className="border border-slate-200 px-2 py-1 text-right">
                            {r.amount.toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-emerald-100 font-bold">
                        <td
                          colSpan={8}
                          className="border border-slate-200 px-2 py-1 text-right"
                        >
                          FINAL TOTAL
                        </td>
                        <td className="border border-slate-200 px-2 py-1 text-right">
                          ₹{" "}
                          {finalTotal.toLocaleString("en-IN", {
                            maximumFractionDigits: 2,
                          })}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                )}
              </div>

              {/* FINAL SAVE BUTTON */}
              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={handleSaveFinalRa}
                  disabled={saving || !finalRows.length}
                  className={cx(
                    "px-4 py-2 rounded-lg text-xs font-semibold text-white",
                    saving || !finalRows.length
                      ? "bg-emerald-300 cursor-not-allowed"
                      : "bg-emerald-700 hover:bg-emerald-800"
                  )}
                >
                  {saving ? "Saving & Generating PDF…" : "Save Final RA & PDF"}
                </button>
              </div>

              {/* FINAL RA HISTORY */}
              <div className="mt-6 border border-emerald-200 rounded-2xl bg-white shadow-sm overflow-hidden">
                <div className="px-3 py-2 bg-slate-900 text-[10px] text-white font-semibold flex justify-between">
                  <span>Final RA Bills (CEO Approved)</span>
                </div>
                <table className="min-w-full text-[10px] border-collapse">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="border border-slate-300 px-2 py-1">
                        Sr
                      </th>
                      <th className="border border-slate-300 px-2 py-1 text-left">
                        Final RA No
                      </th>
                      <th className="border border-slate-300 px-2 py-1">
                        Date
                      </th>
                      <th className="border border-slate-300 px-2 py-1 text-right">
                        Amount
                      </th>
                      <th className="border border-slate-300 px-2 py-1">
                        Download
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {finalRas.length === 0 ? (
                      <tr>
                        <td
                          colSpan={5}
                          className="border border-slate-300 px-2 py-2 text-center text-slate-500"
                        >
                          No Final RA generated yet.
                        </td>
                      </tr>
                    ) : (
                      finalRas.map((b, idx) => (
                        <tr
                          key={b.id}
                          className="odd:bg-white even:bg-slate-50"
                        >
                          <td className="border border-slate-300 px-2 py-1 text-center">
                            {idx + 1}
                          </td>
                          <td className="border border-slate-300 px-2 py-1">
                            {b.raNo || "—"}
                          </td>
                          <td className="border border-slate-300 px-2 py-1 text-center">
                            {b.raDate || "—"}
                          </td>
                          <td className="border border-slate-300 px-2 py-1 text-right">
                            ₹{" "}
                            {(b.totalAmount || 0).toLocaleString("en-IN", {
                              maximumFractionDigits: 2,
                            })}
                          </td>
                          <td className="border border-slate-300 px-2 py-1 text-center">
                            <button
                              type="button"
                              onClick={() =>
                                generateFinalRaPdf(tender, b)
                              }
                              className="px-2 py-0.5 rounded bg-slate-900 text-white"
                            >
                              PDF
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

/* ---------- FINAL RA PDF (same as pehle) ---------- */
function generateFinalRaPdf(tender, bill) {
  try {
    const doc = new jsPDF("p", "mm", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();
    const marginLeft = 15;
    const marginRight = 15;
    const contentWidth = pageWidth - marginLeft - marginRight;

    const title =
      tender?.title ||
      tender?.projectName ||
      "Final Running Account Bill";
    const pacsName =
      tender?.ownerCompany ||
      tender?.pacsName ||
      tender?.pacsShortName ||
      "";

    const raNo = bill.raNo || "";
    const raDate =
      bill.raDate ||
      (bill.createdAt?.toDate
        ? dayjs(bill.createdAt.toDate()).format("DD/MM/YYYY")
        : dayjs().format("DD/MM/YYYY"));

    const contractor = bill.contractorName || "";

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text("Final Running Account Bill", pageWidth / 2, 15, {
      align: "center",
    });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);

    const titleLines = doc.splitTextToSize(title, contentWidth);
    doc.text(titleLines, marginLeft, 22);
    let y = 22 + titleLines.length * 4;

    if (pacsName) {
      doc.text(`PACS: ${pacsName}`, marginLeft, y);
      y += 4;
    }

    doc.text(`Final RA No: ${raNo}`, marginLeft, y);
    doc.text(`Date: ${raDate}`, pageWidth - marginRight, y, {
      align: "right",
    });
    y += 4;
    if (contractor) {
      doc.text(`Contractor: ${contractor}`, marginLeft, y);
      y += 4;
    }
    if (bill.contractorRaNo || bill.engineerRaNo) {
      doc.text(
        `Based on Contractor RA: ${bill.contractorRaNo || "-"} and Engineer RA: ${
          bill.engineerRaNo || "-"
        }`,
        marginLeft,
        y
      );
      y += 5;
    }

    const body = (bill.items || []).map((it, idx) => [
      idx + 1,
      it.description || "",
      it.unit || "",
      (it.boqQty || 0).toFixed(3),
      (it.contractorQty || 0).toFixed(3),
      (it.engineerQty || 0).toFixed(3),
      (it.finalQty || 0).toFixed(3),
      (it.rate || 0).toFixed(2),
      (it.amount || 0).toFixed(2),
    ]);

    autoTable(doc, {
      startY: y,
      margin: { left: marginLeft, right: marginRight },
      head: [
        [
          "Sr",
          "Description",
          "Unit",
          "BOQ",
          "Contractor Qty",
          "Engineer Qty",
          "Final Qty",
          "Rate",
          "Amount",
        ],
      ],
      body,
      theme: "grid",
      styles: {
        fontSize: 8,
        cellPadding: 1.5,
        textColor: [0, 0, 0],
        lineWidth: 0.25,
        lineColor: [0, 0, 0],
      },
      headStyles: {
        fillColor: [230, 230, 230],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        halign: "center",
      },
      columnStyles: {
        0: { cellWidth: 8, halign: "center" },
        1: { cellWidth: 60 },
        2: { cellWidth: 10, halign: "center" },
        3: { cellWidth: 15, halign: "right" },
        4: { cellWidth: 18, halign: "right" },
        5: { cellWidth: 18, halign: "right" },
        6: { cellWidth: 18, halign: "right" },
        7: { cellWidth: 15, halign: "right" },
        8: { cellWidth: 22, halign: "right" },
      },
    });

    const tableEndY = doc.lastAutoTable.finalY || y + 40;
    const totalAmount = bill.totalAmount || 0;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text(
      `FINAL TOTAL: Rs. ${totalAmount.toFixed(2)}`,
      pageWidth - marginRight,
      tableEndY + 8,
      { align: "right" }
    );

    const sigY = tableEndY + 26;
    const contentWidth2 = contentWidth;
    const sigWidth = (contentWidth2 - 20) / 3;
    const x1 = marginLeft;
    const x2 = marginLeft + sigWidth + 10;
    const x3 = marginLeft + 2 * (sigWidth + 10);

    doc.setLineWidth(0.4);
    doc.line(x1, sigY, x1 + sigWidth, sigY);
    doc.line(x2, sigY, x2 + sigWidth, sigY);
    doc.line(x3, sigY, x3 + sigWidth, sigY);

    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text(
      "Contractor / Agency",
      x1 + sigWidth / 2,
      sigY + 4,
      { align: "center" }
    );
    doc.text(
      "PMC Engineer",
      x2 + sigWidth / 2,
      sigY + 4,
      { align: "center" }
    );
    doc.text("PMC CEO", x3 + sigWidth / 2, sigY + 4, {
      align: "center",
    });

    doc.save(`${raNo || "Final_RA"}.pdf`);
  } catch (e) {
    console.error(e);
    toast.error("Final RA PDF generation failed.");
  }
}
